import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  CreatePipelineStageInput,
  PipelineStage,
  UpdatePipelineStageInput,
} from "@school-sales-ai/shared";
import {
  createPipelineStageApi,
  deletePipelineStageApi,
  listPipelineStagesApi,
  reorderPipelineStagesApi,
  updatePipelineStageApi,
} from "@/lib/api";

const STAGES_KEY = ["pipeline-stages"];

export function usePipelineStagesQuery(): ReturnType<
  typeof useQuery<PipelineStage[]>
> {
  return useQuery<PipelineStage[]>({
    queryKey: STAGES_KEY,
    queryFn: listPipelineStagesApi,
  });
}

// Moving/renaming/adding/removing columns changes what the pipeline and lead
// views render, so every mutation invalidates the stages + lead caches.
function useInvalidateStages(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: STAGES_KEY });
    void queryClient.invalidateQueries({ queryKey: ["pipeline"] });
    void queryClient.invalidateQueries({ queryKey: ["leads"] });
  };
}

export function useCreatePipelineStage(): ReturnType<
  typeof useMutation<PipelineStage, Error, CreatePipelineStageInput>
> {
  const invalidate = useInvalidateStages();
  return useMutation<PipelineStage, Error, CreatePipelineStageInput>({
    mutationFn: createPipelineStageApi,
    onSuccess: () => {
      toast.success("Columna agregada.");
      invalidate();
    },
    onError: (error) => toast.error(error.message ?? "Error al agregar la columna."),
  });
}

type UpdateArgs = { id: string; data: UpdatePipelineStageInput };

export function useUpdatePipelineStage(): ReturnType<
  typeof useMutation<PipelineStage, Error, UpdateArgs>
> {
  const invalidate = useInvalidateStages();
  return useMutation<PipelineStage, Error, UpdateArgs>({
    mutationFn: ({ id, data }) => updatePipelineStageApi(id, data),
    onSuccess: () => {
      toast.success("Columna actualizada.");
      invalidate();
    },
    onError: (error) => toast.error(error.message ?? "Error al actualizar la columna."),
  });
}

export function useDeletePipelineStage(): ReturnType<
  typeof useMutation<void, Error, string>
> {
  const invalidate = useInvalidateStages();
  return useMutation<void, Error, string>({
    mutationFn: deletePipelineStageApi,
    onSuccess: () => {
      toast.success("Columna eliminada.");
      invalidate();
    },
    onError: (error) => toast.error(error.message ?? "Error al eliminar la columna."),
  });
}

export function useReorderPipelineStages(): ReturnType<
  typeof useMutation<PipelineStage[], Error, string[]>
> {
  const invalidate = useInvalidateStages();
  return useMutation<PipelineStage[], Error, string[]>({
    mutationFn: reorderPipelineStagesApi,
    onSuccess: () => invalidate(),
    onError: (error) => toast.error(error.message ?? "Error al reordenar las columnas."),
  });
}
